import conciergePlanMessages from '@/components/concierge/plan/beta/messages';
import { useAiFeedback, useAiFeedbackQuery } from '@/hooks/use-ai-feedback';
import useToastDialog from '@/hooks/use-toast-dialog';
import { styleInfoColor } from '@/utils/styles';
import { FeedbackRequestFeedbackTargetTypeEnum } from '@calm/evexia-web-sdk';
import { Check, ThumbUp, ThumbDown } from '@mui/icons-material';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import {
    Box,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Typography,
} from '@rhg/libs/material';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

const styleConfirmContainer = {
    color: styleInfoColor,
    backgroundColor: 'info.light',
    height: 48,
    width: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

export interface FeedbackMenuProps {
    /**
     * The content ID to submit feedback for.
     * For programs, this should be the programId.
     * For guides, this should be the guideId.
     */
    contentId: string;
}

export function FeedbackMenu({ contentId }: FeedbackMenuProps) {
    const { contentFeedbackId } =
        useProvidedProgramContextWithGuidesAndWorkflow();
    const { submitFeedbackAsync } = useAiFeedback();
    const { data: existingFeedback } = useAiFeedbackQuery(contentFeedbackId);
    const { toast } = useToastDialog();
    const { formatMessage } = useIntl();

    const hasFeedbackForContent = useMemo(
        () =>
            contentId &&
            existingFeedback?.data?.feedback?.some(
                (f) =>
                    f.feedbackTargetId === contentId &&
                    f.feedbackTargetType ===
                        FeedbackRequestFeedbackTargetTypeEnum.ContentRecSingle
            ),
        [contentId, existingFeedback?.data?.feedback]
    );

    const handleFeedback = useCallback(
        async (useful: boolean) => {
            if (!contentFeedbackId || !contentId) {
                return;
            }

            try {
                await submitFeedbackAsync({
                    responseId: contentFeedbackId,
                    contentId,
                    useful,
                    feedbackTargetType:
                        FeedbackRequestFeedbackTargetTypeEnum.ContentRecSingle,
                });

                toast({
                    title: formatMessage(
                        conciergePlanMessages.conciergePlanRatingAnswerSubmitted
                    ),
                    icon: (
                        <Box sx={styleConfirmContainer}>
                            <Check color="inherit" fontSize="large" />
                        </Box>
                    ),
                });
            } catch (error) {
                console.error('Failed to submit feedback:', error);
            }
        },
        [
            contentFeedbackId,
            contentId,
            submitFeedbackAsync,
            toast,
            formatMessage,
        ]
    );

    if (!contentFeedbackId || !contentId || hasFeedbackForContent) {
        return null;
    }

    return (
        <>
            <MenuItem
                onClick={() => handleFeedback(true)}
                trackButton="NotMapped"
            >
                <ListItemIcon>
                    <ThumbUp />
                </ListItemIcon>
                <ListItemText>
                    <Typography variant="body1" color="text.body">
                        {formatMessage(
                            conciergePlanMessages.conciergePlanFeedbackRelatedToGoal
                        )}
                    </Typography>
                </ListItemText>
            </MenuItem>
            <MenuItem
                onClick={() => handleFeedback(false)}
                trackButton="NotMapped"
            >
                <ListItemIcon>
                    <ThumbDown />
                </ListItemIcon>
                <ListItemText>
                    <Typography variant="body1" color="text.body">
                        {formatMessage(
                            conciergePlanMessages.conciergePlanFeedbackNotRelatedToGoal
                        )}
                    </Typography>
                </ListItemText>
            </MenuItem>
        </>
    );
}
